import { Controller } from '@hotwired/stimulus';
import { STAGE_BP_MOBILE_MAX, STAGE_BP_TABLET_MAX } from '../js/stage_breakpoints';

/* stimulusFetch: 'eager' */

export default class extends Controller {
    static targets = ['video'];

    static values = {
        mobileUrl: String,
        tabletUrl: String,
        desktopUrl: String,
    };

    connect() {
        this.currentBreakpoint = null;
        this.resizeTimeoutId = null;
        this.onResize = () => {
            clearTimeout(this.resizeTimeoutId);
            this.resizeTimeoutId = setTimeout(() => this.syncVideoSource(), 150);
        };
        this.onMotionChange = () => this.syncVideoSource();
        this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

        if (this.prefersReducedMotion() || !this.getUrlForBreakpoint(this.getBreakpoint())) {
            this.showFallback();
        }
        this.syncVideoSource();

        window.addEventListener('resize', this.onResize);
        if (this.reducedMotionQuery.addEventListener) {
            this.reducedMotionQuery.addEventListener('change', this.onMotionChange);
        } else {
            this.reducedMotionQuery.addListener(this.onMotionChange);
        }
    }

    disconnect() {
        window.removeEventListener('resize', this.onResize);
        clearTimeout(this.resizeTimeoutId);
        this.resizeTimeoutId = null;

        if (this.reducedMotionQuery) {
            if (this.reducedMotionQuery.removeEventListener) {
                this.reducedMotionQuery.removeEventListener('change', this.onMotionChange);
            } else {
                this.reducedMotionQuery.removeListener(this.onMotionChange);
            }
        }
        this.clearVideo();
    }

    syncVideoSource() {
        if (!this.hasVideoTarget) {
            return;
        }

        if (this.prefersReducedMotion()) {
            this.currentBreakpoint = null;
            this.clearVideo();
            this.showFallback();
            return;
        }

        const breakpoint = this.getBreakpoint();
        const url = this.getUrlForBreakpoint(breakpoint);

        if (breakpoint === this.currentBreakpoint && this.videoTarget.getAttribute('src') === (url || null)) {
            return;
        }

        this.currentBreakpoint = breakpoint;

        if (!url) {
            this.clearVideo();
            this.showFallback();
            return;
        }

        this.loadVideo(url);
    }

    loadVideo(url) {
        const video = this.videoTarget;

        this.showFallback();

        video.onerror = () => {
            this.clearVideo();
            this.showFallback();
        };

        video.onplaying = () => {
            this.hideFallback();
        };

        video.muted = true;
        video.setAttribute('src', url);
        video.load();

        const playPromise = video.play();
        if (playPromise !== undefined) {
            playPromise
                .then(() => {
                    if (!video.paused) {
                        this.hideFallback();
                    }
                })
                .catch((error) => {
                    if (error?.name === 'AbortError') {
                        return;
                    }
                    this.clearVideo();
                    this.showFallback();
                });
        }
    }

    clearVideo() {
        if (!this.hasVideoTarget) {
            return;
        }

        const video = this.videoTarget;
        video.onerror = null;
        video.onplaying = null;
        video.pause();
        video.removeAttribute('src');
        video.load();
    }

    getBreakpoint() {
        const width = window.innerWidth;
        if (width <= STAGE_BP_MOBILE_MAX) {
            return 'mobile';
        }
        if (width <= STAGE_BP_TABLET_MAX) {
            return 'tablet';
        }
        return 'desktop';
    }

    getUrlForBreakpoint(breakpoint) {
        const urls = {
            mobile: this.mobileUrlValue,
            tablet: this.tabletUrlValue,
            desktop: this.desktopUrlValue,
        };

        return (urls[breakpoint] || '').trim();
    }

    prefersReducedMotion() {
        return this.reducedMotionQuery?.matches === true;
    }

    hideFallback() {
        this.element.classList.add('stage-image--video-playing');
    }

    showFallback() {
        this.element.classList.remove('stage-image--video-playing');
    }
}
